<template>
  <div class="page-container expense-tables-page">
    <portal to="page-name">Tabelas de preços</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Tabelas de preços</h1>
        <div class="columns">
          <div class="column">
            <div class="text-dark-gray mt-1">Gerencie tabelas de preços dos planos de saúde</div>
          </div>
          <div class="column text-right">
            <button class="btn btn-primary btn-icon btn-icon-left mr-2"
                    :disabled="deleting"
                    @click="open">
              <fa-icon :icon="['fal', 'plus']"/>Nova tabela
            </button>
            <dx-dropdown
            class="btn btn-icon btn-action btn-neutral drop-menu"
            :class="{loading: printing}"
            :items="[{ key: 'print', name: 'Imprimir' }, { key: 'sheet', name: 'Planilha' }]"
            :disabled="printing || deleting"
            @select="print"
            label="name"
            direction="right"
            >
            <fa-icon :icon="['fal', 'print']" v-if="!printing" />
            </dx-dropdown>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column col-2 col-md-6 col-sm-12 form-group">
            <label class="form-label">Tipo</label>
            <select class="form-select"
                    :disabled="deleting"
                    @change="find"
                    v-model="filter.type">
              <option value="">Todos os tipos</option>
              <option v-for="(text, value) in tableTypes"
                      :value="value" :key="value">{{ text }}</option>
            </select>
          </div>
          <div class="column col-4 form-group"
               :class="{'has-error': $v.filter.startDate.$error || $v.filter.endDate.$error}">
            <label class="form-label">Período de vigência</label>
            <div class="input-group">
              <dx-input-date
                id="start-date"
                class="form-input text-center"
                v-model="filter.startDate"
                :disabled="deleting"
                @blur="$v.filter.startDate.$touch()"
              />
              <dx-input-date
                id="end-date"
                class="form-input text-center"
                v-model="filter.endDate"
                :disabled="deleting"
                @blur="$v.filter.endDate.$touch()"
              />
              <button class="btn btn-icon btn-action btn-neutral input-group-btn"
                      :disabled="deleting"
                      @click="clear">
                <fa-icon :icon="['fal', 'times']" />
              </button>
            </div>
          </div>
          <div class="column form-group">
            <label class="form-label">Nome da tabela</label>
            <input type="text" class="form-input"
                   :disabled="deleting"
                   @keydown.enter="find"
                   v-model="filter.search"
                   placeholder="Pesquisar por nome da tabela">
          </div>
          <div class="column col-auto mb-2" style="align-self: end">
            <button class="btn btn-gray btn-icon btn-icon-left input-group-btn"
                    :disabled="loading || deleting"
                    :class="{loading}"
                    @click="find">
              <fa-icon :icon="['fal', 'search']"/> Pesquisar
            </button>
          </div>
        </div>
        <div class="loading loading-lg mt-2" v-if="loading" />
        <template v-else>
          <div class="empty" v-if="data.items.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Tabelas</p>
            <p class="empty-subtitle">
              Nenhuma tabela encontrado. Verifique os filtros para realizar novas buscas
            </p>
          </div>
          <table class="table table-striped table-hover" v-else>
            <thead>
            <tr>
              <th class="hide-sm" style="width: 40px">#</th>
              <th>Nome</th>
              <th style="width: 110px">Tipo</th>
              <th class="text-center" style="width: 110px">Vig. início</th>
              <th class="text-center" style="width: 110px">Vig. término</th>
              <th style="width: 70px"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in data.items" :key="i">
              <td class="hide-sm">{{ i + 1 }}</td>
              <td>
                <span class="hover-link" v-if="deleting">{{ item.name }}</span>
                <router-link :to="editLink(item)" v-else>
                  {{ item.name }}
                </router-link>
              </td>
              <td>{{ getTypeName(item.type) }}</td>
              <td class="text-center">{{ item.startDate | date }}</td>
              <td class="text-center">{{ item.endDate | date }}</td>
              <td class="text-right">
                <dx-dropdown
                  class="btn btn-icon btn-action btn-gray-outline drop-menu input-group-addon"
                  :class="{loading: idx === i && (duplicating || deleting)}"
                  :items="menuItems"
                  :disabled="deleting || duplicating"
                  @select="selectedMenu($event, item)"
                  label="name"
                  direction="right"
                >
                  <fa-icon :icon="['fal', 'bars']" />
                </dx-dropdown>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
      <div class="card-footer text-center" v-if="data.items.length > 0">
        <button
          v-if="data.hasMore && !loading"
          class="btn btn-primary btn-fixed-width"
          @click="loadMore"
          :disabled="loadingMore"
          :class="{ loading: loadingMore }"
        >Carregar mais</button>
      </div>
    </div>
  </div>
</template>

<script>
import { types as tableTypes, getName as getTypeName } from 'src/data/table-types';
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { date, minDate } from '@/data/validators';
import {
  PRINT_EXPENSE_TABLE_LIST,
  DELETE_EXPENSE_TABLE,
} from '@/data/actions/modules/general';
import { mapState } from 'vuex';

export default {
  data() {
    return {
      idx: null,
      loading: false,
      loadingMore: false,
      deleting: false,
      printing: false,
      duplicating: false,
      getTypeName,
      tableTypes,
      periodOptions: [],
      menuItems: [
        { key: 'edit', name: 'Editar' },
        { key: 'duplicate', name: 'Duplicar' },
        { key: 'remove', name: 'Remover' },
      ],
      filter: {
        type: '',
        search: '',
        startDate: '',
        endDate: '',
      },
      data: {
        items: [],
        hasMore: false,
        limit: 100,
        offset: 0,
      },
    };
  },
  mounted() {
    this.init();
  },
  validations() {
    const rules = {
      filter: {
        startDate: { date },
        endDate: { date },
      },
    };

    if (this.filter.startDate && moment(this.filter.startDate).isValid()) {
      rules.filter.endDate = {
        required,
        date,
        minDate: minDate(this.filter.startDate),
      };
    }

    return rules;
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    canPrintAccess() {
      return this.$can(PRINT_EXPENSE_TABLE_LIST);
    },
    canRemoveAccess() {
      return this.$can(DELETE_EXPENSE_TABLE);
    },
  },
  methods: {
    init() {
      this.load();
    },
    async find() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) {
        return;
      }

      this.data.offset = 0;
      await this.load();
    },
    async loadMore() {
      this.data.offset += this.data.limit;
      await this.load();
    },
    load() {
      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        active: true,
        limit: this.data.limit,
        offset: this.data.offset,
      };

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.startDate) {
        params.startDate = this.filter.startDate;
      }

      if (this.filter.endDate) {
        params.endDate = this.filter.endDate;
      }

      if (this.filter.search.trim().length > 0) {
        params.search = this.filter.search;
      }

      return this.$http.get('/expense-tables', { params })
        .then(({ data }) => {
          this.data.hasMore = data.hasMore;
          this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    open() {
      this.$router.push('/clinical/expense-tables/create');
    },
    clear() {
      this.filter.startDate = '';
      this.filter.endDate = '';
      this.find();
    },
    print({ key }) {
      if (!this.canPrintAccess) {
        this.$toast.error('Acesso não autorizado!');
        return null;
      }

      this.printing = true;

      const params = {
        active: true,
      };

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.startDate) {
        params.startDate = this.filter.startDate;
      }

      if (this.filter.endDate) {
        params.endDate = this.filter.endDate;
      }

      if (this.filter.search.trim().length > 0) {
        params.search = this.filter.search;
      }

      if (key === 'sheet') {
        return this.$file
          .download('/expense-tables/print', params, {
            method: 'POST',
            name: 'Lista de tabelas.xlsx',
          })
          .catch(() => {})
          .finally(() => {
            this.printing = false;
          });
      }

      return this.$file
        .print('/expense-tables/print', params)
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao imprimir! Tente novamente mais tarde.');
        })
        .finally(() => {
          this.printing = false;
        });
    },
    editLink(item) {
      return `/clinical/expense-tables/${item.id}/edit`;
    },
    edit(item) {
      this.$router.push(`/clinical/expense-tables/${item.id}/edit`);
    },
    duplicate(item) {
      if (this.duplicating) {
        return null;
      }

      this.idx = this.data.items.indexOf(item);
      this.duplicating = true;

      return this.$http.put(`/expense-tables/${item.id}/duplicate`)
        .then(() => {
          this.load();
        })
        .catch(() => {
          this.$toast.error('Erro ao duplicar a tabela!');
        })
        .finally(() => {
          this.duplicating = false;
        });
    },
    remove(item) {
      if (!this.canRemoveAccess) {
        this.$toast.error('Acesso não autorizado!');
        return;
      }

      if (this.deleting) {
        return;
      }

      this.$dialog.show('', {
        size: 'md',
        html: `<div class="text-center">
                <p class="text-center h4">Atenção!</p>
                <p>Ao confirmar a remoção,
                 <strong class="text-primary">todos os valores</strong>
                 serão excluídos.</p>
                <div class="h6 text-error text-bold">
                 Essa ação é irreversível. Deseja continuar?
                </div>
                <br>
                <div>Responsável: <strong>${this.user.name}</strong></div>
                <div>
                  Data e hora: <strong>${moment().format('DD/MM/YYYY HH:mm')}</strong>
                </div>
              </div>`,
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.deleting = true;
              const idx = this.data.items.indexOf(item);
              this.idx = idx;
              close();
              this.$http
                .delete(`/expense-tables/${item.id}`)
                .then(() => {
                  this.data.items.splice(idx, 1);
                })
                .catch((e) => {
                  let errorMessage = 'Ocorreu um erro ao realizar a impressão!';
                  if (e.response) {
                    if (e.response.status === 403) {
                      errorMessage = 'Acesso não autorizado';
                    } else if (e.response.data && e.response.data.message) {
                      errorMessage = e.response.data.message;
                    }
                  }
                  this.$toast.error(errorMessage);
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    selectedMenu({ key }, item) {
      if (key === 'edit') {
        this.edit(item);
      } if (key === 'duplicate') {
        this.duplicate(item);
      } if (key === 'remove') {
        this.remove(item);
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';
.expense-tables-page {
  .drop-menu {
    line-height: 0;
    .menu {
      color: $dark-color;
      line-height: $layout-spacing-xl;
      text-align: left;
    }
    //.menu .menu-item > a, .menu .menu-item > .hover-link {
    //  color: $dark-color;
    //  text-align: left;
    //}
  }
}
</style>
